import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import React, { useMemo } from "react";

import {
  FilterMenu,
  FilterSearch,
  Filter,
  FilterRadioButton,
  FilterCheckbox,
} from "@ofqual/portal-components/components/ResponseFilter";

import { useLookup, useSearch, useUrl } from "@ofqual/portal-core/core/hooks";
import { Select } from "@ofqual/portal-components/components/Select";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import {
  aoService,
  aoServiceSearch,
  caoStatusService,
  usersService,
  cachedRequestTypeLookupService,
} from "../../services";
import { Opts, QueryParams } from "@ofqual/portal-core/core/types";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";
import { UserType } from "../../redux/types";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

export const FilterControls: React.FC = () => {
  const classes = useStyles();

  const {
    navigateToNextSearch,
    searchParamsFiltersOnly,
    searchParams,
  } = useSearch(useUrl());
  const { token, userType } = useSelector(authSelector);

  const isOfqualUser = userType === UserType.Ofqual;

  const [caoStatusLookups, usersLookups, requestTypeLookups] = useLookup(
    caoStatusService,
    {
      service: { getAll: (opts: Opts) => usersService.getAll({}, opts) },
      id: "upn",
      label: "fullName",
    },
    cachedRequestTypeLookupService
  );

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoServiceSearch.search(params, { token }),
    }),
    [token]
  );

  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParamsFiltersOnly.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"search"}
      />
      &nbsp;&nbsp;
      <Filter
        Icon={FilterList}
        title="Order By"
        badge={!!searchParams.order}
        dataTest="org-list-order-filter"
      >
        <FilterRadioButton
          isChecked={typeof searchParams.order !== "string"}
          value={undefined}
          text={"Created date"}
          onChange={() =>
            navigateToNextSearch({
              order: undefined,
              orderDirection: undefined,
            })
          }
        />
         <FilterRadioButton
          isChecked={searchParams.order === "LastResponseDate"}
          value={"LastResponseDate"}
          text={"Last response date"}
          onChange={() =>
            navigateToNextSearch({
              order: "LastResponseDate",
              orderDirection: "desc",
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "MinDueDate"}
          value={"MinDueDate"}
          text={"Due date (ascending)"}
          onChange={() =>
            navigateToNextSearch({
              order: "MinDueDate",
              orderDirection: "asc",
            })
          }
        />
      </Filter>
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="comms-filter-menu"
        search={{ searchParamsFiltersOnly, navigateToNextSearch }}
      >
        {(workingParams, dirtyParams, addParam, updateMultipleParams) => (
          <form>
            {isOfqualUser ? (
              <>
                <MenuItem className={classes.menuItem}>
                  <SearchField
                    searchFieldVariant="multiple"
                    className={
                      dirtyParams.includes("awardingOrgIds")
                        ? classes.root
                        : undefined
                    }
                    onChange={(e: any) =>
                      addParam("awardingOrgIds", e.target.value)
                    }
                    value={workingParams.awardingOrgIds}
                    label="Awarding Organisations"
                    datatest="filter-aos"
                    service={mappedOrgLookupService}
                  />
                </MenuItem>
                <MenuItem className={classes.menuItem}>
                  <Select
                    className={
                      dirtyParams.includes("status") ? classes.root : undefined
                    }
                    label="Status"
                    data-test="status-filter"
                    value={workingParams.status}
                    onChange={(e: any) => addParam("status", e.target.value)}
                  >
                    {caoStatusLookups}
                  </Select>
                </MenuItem>
              </>
            ) : null}
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("type") ? classes.root : undefined
                }
                label="Request type"
                data-test="request-type-filter"
                selectVariant="multiple"
                value={ensureParamIsArray(workingParams.type)}
                onChange={(e: any) => addParam("type", e.target.value)}
              >
                {requestTypeLookups.map((x) => ({
                  id: x.id.toString(),
                  label: x.label,
                }))}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <DatePicker
                label="Submitted From:"
                value={workingParams.submittedFrom || null}
                onChange={(val: any) => addParam("submittedFrom", val)}
                onClear={() => addParam("submittedFrom", undefined)}
                returnDayBoundary="startOfDay"
                dataTest="submitted-from-filter"
              />
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <DatePicker
                label="Submitted To:"
                value={workingParams.submittedTo || null}
                onChange={(val: any) => addParam("submittedTo", val)}
                onClear={() => addParam("submittedTo", undefined)}
                returnDayBoundary="endOfDay"
                dataTest="submitted-to-filter"
              />
            </MenuItem>
            <FilterCheckbox
              isChecked={workingParams.unassigned === "true"}
              name="unassigned"
              text="Unassigned"
              onChange={() => {
                updateMultipleParams({
                  assignedTo: undefined,
                  unassigned: workingParams.unassigned ? undefined : "true",
                });
              }}
            />
            {isOfqualUser ? (
              <MenuItem className={classes.menuItem}>
                <Select
                  className={
                    dirtyParams.includes("assignedTo")
                      ? classes.root
                      : undefined
                  }
                  label="Assigned to"
                  data-test="assigned-to-filter"
                  selectVariant="multiple"
                  value={ensureParamIsArray(workingParams.assignedTo)}
                  onChange={(e: any) => addParam("assignedTo", e.target.value)}
                  disabled={workingParams.unassigned === "true"}
                >
                  {usersLookups}
                </Select>
              </MenuItem>
            ) : null}
            {!isOfqualUser ? (
              <FilterCheckbox
                isChecked={workingParams.unread === "true"}
                name="unread"
                text="Unread"
                onChange={() => {
                  addParam("unread", workingParams.unread ? undefined : "true");
                }}
              />
            ) : null}
          </form>
        )}
      </FilterMenu>
    </>
  );
};
